/**
 * Turns `/:item/:tab?` into a screen. Only routes present in GET /me → `menu` open; anything else goes
 * to the caller's `home_route` (prototype `render()`: not allowed → `homeRoute()`). The server still
 * enforces every read — this only keeps the menu and the address bar in step.
 *
 * Hub items (2+ tabs) render the prototype's `hubRender()`: page head with the hub name on a phone and the
 * tab name on a desktop, the phone tab row, then `.hubbody.hub-<key>` holding the tab's screen.
 */
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router';
import type { MenuItem, MenuTab } from '@amplispot/contracts';
import { useSession } from '../lib/session';
import { Empty } from '../ui/kit';
import { PAGE_SUB } from './copy';
import { PageInfo } from './Help';
import { TeamSpocs } from '../screens/team/TeamSpocs';
import { Settings } from '../screens/settings/Settings';
import { ClientBook } from '../screens/clients/ClientBook';
import { AllWork } from '../screens/work/AllWork';
import { MyRequests } from '../screens/requests/MyRequests';
import { ManagerToday } from '../screens/today/ManagerToday';
import { MyDay } from '../screens/today/MyDay';
import { TimesheetReport } from '../screens/timesheets/TimesheetReport';
import { CatchUp } from '../screens/timesheets/CatchUp';
import { HolidayCalendar } from '../screens/holidays/HolidayCalendar';
import { ReviewQueue } from '../screens/team/ReviewQueue';
import { Dropped } from '../screens/team/Dropped';
import { TeamPlan } from '../screens/team/TeamPlan';
import { AtRisk } from '../screens/team/AtRisk';
import { Briefs } from '../screens/team/Briefs';
import { Targets } from '../screens/clients/Targets';
import { Engagement } from '../screens/clients/Engagement';
import { UsageOverview } from '../screens/usage/UsageOverview';
import { UsageReports } from '../screens/usage/UsageReports';
import { UsageAlerts } from '../screens/usage/UsageAlerts';
import { ClientUsagePage } from '../screens/usage/ClientUsage';
import { Checklists } from '../screens/checklists/Checklists';
import { Documents } from '../screens/clients/Documents';
import { ClientDeck } from '../screens/clients/ClientDeck';
import { Meetings } from '../screens/clients/Meetings';
import { Opportunities } from '../screens/clients/Opportunities';
import { Emails } from '../screens/clients/Emails';
import { Capture } from '../screens/capture/Capture';
import { RevenueUsage } from '../screens/money/RevenueUsage';
import { UsageBilling } from '../screens/money/UsageBilling';
import { RiskFlags } from '../screens/money/RiskFlags';
import { Ideas } from '../screens/ideas/Ideas';
import { DailyUpdate } from '../screens/team/DailyUpdate';
import { CreatedToday } from '../screens/team/CreatedToday';
import { AppraisalRecord } from '../screens/team/AppraisalRecord';
import { Workload } from '../screens/team/Workload';

/**
 * Screens built so far, by route key. Everything else in the menu shows the "later step" state.
 */
const SCREENS: Record<string, () => ReactNode> = {
  'clients/book': () => <ClientBook />,
  'work/allwork': () => <AllWork />,
  today: () => <ManagerToday />,
  'me/day': () => <MyDay />,
  'team/spocs': () => <TeamSpocs />,
  'team/review': () => <ReviewQueue />,
  'team/dropped': () => <Dropped />,
  'team/plan': () => <TeamPlan />,
  settings: () => <Settings />,
  'my-requests': () => <MyRequests />,
  'team/tsreport': () => <TimesheetReport />,
  holidays: () => <HolidayCalendar />,
  'team/atrisk': () => <AtRisk />,
  'team/briefs': () => <Briefs />,
  'clients/targets': () => <Targets />,
  'clients/engage': () => <Engagement />,
  'clients/meetings': () => <Meetings />,
  'clients/opps': () => <Opportunities />,
  'clients/emails': () => <Emails />,
  'usage/uover': () => <UsageOverview />,
  'usage/ualerts': () => <UsageAlerts />,
  'usage/usagerep': () => <UsageReports />,
  'me/checklists': () => <Checklists />,
  'team/checklists': () => <Checklists />,
  'clients/documents': () => <Documents />,
  'money/admin': () => <RevenueUsage />,
  'money/usage': () => <UsageBilling />,
  'money/flags': () => <RiskFlags />,
  // Members and requesters: a page of its own; the founder and the AD decide on it as a Team tab (hub head).
  ideas: () => <Ideas />,
  'team/ideas': () => <Ideas hub />,
  // Team reads (t-75b4cb9e): the managers' tabs, and the member's own Done today / My record on the same reads.
  'team/daily': () => <DailyUpdate />,
  'team/newtoday': () => <CreatedToday />,
  'team/record': () => <AppraisalRecord />,
  'team/workload': () => <Workload />,
  'me/daily': () => <DailyUpdate />,
  'me/record': () => <AppraisalRecord />,
};

/** Older step-3 links (`/work/requests?request=…`) land on All work with that type and keep their query. */
const TAB_TYPE: Record<string, string> = { requests: 'request', commitments: 'task', escalations: 'escalation', approvals: 'approval' };

export function Page() {
  const { item: itemKey, tab: tabKey, sub } = useParams();
  const { me, can } = useSession();
  const loc = useLocation();
  const item = me.menu.find((m) => m.key === itemKey);
  // `/my-requests/<id>` (the origin link on a task made from an internal request) opens that request's panel:
  // the requester on My requests, anyone else (Ops) over their home page. The id is never dropped.
  if (itemKey === 'my-requests' && tabKey) {
    return <Navigate to={item ? `/my-requests?request=${encodeURIComponent(tabKey)}` : `${me.home_route}?internal_request=${encodeURIComponent(tabKey)}`} replace />;
  }
  // The timesheet gate (prototype go/render override): a gated member sees only the catch-up, on any route.
  if (me.gate?.timesheet_blocked) return <CatchUp />;
  // Capture is not a menu item: the top bar's Capture button opens it on every screen (prototype `capture` route,
  // allowed for every role but the requester). The server decides with capture.use.
  if (itemKey === 'capture') return can('capture.use') ? <Capture /> : <Navigate to={me.home_route} replace />;
  if (!item) return <Navigate to={me.home_route} replace />;
  // The client deck (prototype `deck`, from "Make client deck" on a client): /clients/deck?client=<id>. Its own head.
  if (item.key === 'clients' && tabKey === 'deck') return <ClientDeck />;
  // One client's usage page (prototype `cusage`): /usage/client/<id>. It has its own head, so no hub tabs.
  if (item.key === 'usage' && tabKey === 'client' && sub) return <ClientUsagePage key={sub} clientId={sub} />;

  if (item.tabs.length === 0) {
    const screen = SCREENS[item.key];
    // Item screens with their own internal tabs (Settings) read the rest of the path themselves.
    return screen ? <>{screen()}</> : <NotYetBuilt title={item.label} sub={PAGE_SUB[item.key]} helpKey={item.key} />;
  }

  const tab = item.tabs.find((t) => t.key === tabKey);
  if (!tab) {
    const q = new URLSearchParams(loc.search);
    const t = tabKey ? TAB_TYPE[tabKey] : undefined;
    if (t && !q.has('type')) q.set('type', t);
    const s = q.toString();
    return <Navigate to={`${item.tabs[0]!.route}${s ? `?${s}` : ''}`} replace />;
  }
  return <Hub item={item} tab={tab} />;
}

/** A hub with a single tab speaks for the whole hub (prototype: the AD's "My day" is the `me` hub). */
const HUB_SUB: Record<string, string> = {
  me: 'Your tasks, today’s plan, and the weekly and monthly routine for your clients.',
  work: 'Tasks, client requests, escalations and approvals in one list. Use the filters to narrow it down.',
};

function Hub({ item, tab }: { item: MenuItem; tab: MenuTab }) {
  const multi = item.tabs.length > 1;
  const screen = SCREENS[`${item.key}/${tab.key}`];
  return (
    <>
      <div className="ph">
        <div>
          <h1>
            {multi ? (
              <>
                <span className="h-hub">{item.label}</span>
                <span className="h-tab">{tab.label}</span>
              </>
            ) : (
              item.label
            )}
            <PageInfo helpKey={tab.key} />
          </h1>
          <p>{(!multi && HUB_SUB[item.key]) || PAGE_SUB[tab.key] || PAGE_SUB[item.key] || ''}</p>
        </div>
      </div>
      {multi ? (
        <div className="tabs hubtabs" role="tablist">
          {item.tabs.map((t) => (
            <Link key={t.key} to={t.route} className={t.key === tab.key ? 'on' : ''} role="tab" aria-selected={t.key === tab.key}>
              {t.label}
              {t.badge ? (
                <span className="n" style={t.badge.tone === 'alert' ? { color: 'var(--cold)', fontWeight: 600 } : undefined}>
                  {t.badge.count}
                </span>
              ) : null}
            </Link>
          ))}
        </div>
      ) : null}
      <div className={`hubbody hub-${item.key}`}>{screen ? screen() : <LaterStep label={tab.label} />}</div>
    </>
  );
}

function LaterStep({ label }: { label: string }) {
  return (
    <div className="panel">
      <Empty title={`${label} arrives in a later build step`}>
        This screen is part of the approved design and is built in order. It will show here as soon as its step ships.
      </Empty>
    </div>
  );
}

function NotYetBuilt({ title, sub, helpKey }: { title: string; sub: string | undefined; helpKey: string }) {
  return (
    <>
      <div className="ph">
        <div>
          <h1>
            {title}
            <PageInfo helpKey={helpKey} />
          </h1>
          {sub ? <p>{sub}</p> : null}
        </div>
      </div>
      <LaterStep label={title} />
    </>
  );
}
